import React, { useState, useMemo, useEffect } from "react";
import styled, { css } from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import {
  Patient,
  PROMPT_TYPE_KOREAN_NAMES,
  getPromptTypeKoreanName,
  AIReviewStatus,
} from "../../common/models/types";
import Text from "../atoms/Text";
import Input from "../atoms/Input";
import Icon from "../atoms/Icon";
import { IconName } from "../atoms/Icon/IconNames";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { calculateAge } from "utils/TimeUtil";
import { fetchPatientReviews } from "../../store/middleware";
import { log, LogLevel } from "utils/LogUtil";
import { Button, ToggleButton, Tag } from "components/atoms";
import { getDateStringFromToday, getDateTimeString } from "utils/TimeUtil";
import Dropdown, { DropdownMenuItem } from "components/molecules/Dropdown";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import MemoEditModal from "components/molecules/MemoEditModal";
import { useAIReviewPin } from "hooks/useAIReviewPin";
import { useAIReviewMemo } from "hooks/useAIReviewMemo";
import { useAIReviewDelete } from "hooks/useAIReviewDelete";
import { useNavigate } from "react-router-dom";
import { resolveNhisTag } from "./PatientSidebar/nhisTag";

const SidebarContainer = styled.aside<{ isOpen: boolean }>`
  width: ${({ isOpen }) => (isOpen ? "260px" : "60px")};
  min-width: ${({ isOpen }) => (isOpen ? "260px" : "60px")};
  max-width: ${({ isOpen }) => (isOpen ? "320px" : "60px")};
  height: calc(100vh - 103px);
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border-right: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease-in-out, min-width 0.3s ease-in-out;
  overflow-x: hidden;
`;

const HeaderSection = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ isOpen }) => (isOpen ? "space-between" : "center")};
  padding: ${({ isOpen }) => (isOpen ? "16px 16px 0px 16px" : "16px 0")};
  min-height: 60px;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchFilterSection = styled.div`
  padding: 10px 16px 16px 16px;
`;

const SearchRow = styled.div`
  margin-bottom: 16px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  background: ${COLOR_SYSTEM.get("Skyblue")?.[50]};
  color: ${COLOR_SYSTEM.get("Skyblue")?.[500]};
  border: 1px solid ${COLOR_SYSTEM.get("Skyblue")?.[200]};
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLOR_SYSTEM.get("Skyblue")?.[50]};
  color: ${COLOR_SYSTEM.get("Skyblue")?.[500]};
  border: 1px solid ${COLOR_SYSTEM.get("Skyblue")?.[200]};
  border-radius: 6px;
  width: 32px;
  height: 32px;
  font-size: 18px;
  cursor: pointer;
`;

const PatientList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 8px 8px 8px;
`;

const PatientCardStyled = styled.div<{
  isPatientOnlySelected: boolean;
  hasSelectedReport: boolean;
}>`
  position: relative;
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border: 1px solid
    ${({ isPatientOnlySelected, hasSelectedReport }) => {
      if (isPatientOnlySelected) return COLOR_SYSTEM.get("Skyblue")?.[300];
      if (hasSelectedReport) return COLOR_SYSTEM.get("Gray")?.[300];
      return COLOR_SYSTEM.get("Gray")?.[200];
    }};
  border-radius: 16px;
  margin-bottom: 16px;
  transition: background 0.2s, border 0.2s, box-shadow 0.2s;
  box-shadow: ${({ isPatientOnlySelected }) =>
    isPatientOnlySelected ? "0 2px 8px 0 rgba(33, 150, 243, 0.10)" : "none"};
`;

const NhisTagAbsolute = styled.span`
  position: absolute;
  top: 12px;
  right: 16px;
  display: inline-flex;
  pointer-events: auto;
  z-index: 1;
`;

const PatientCardHeader = styled.div<{
  isPatientOnlySelected: boolean;
  hasSelectedReport: boolean;
  isLast: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px 20px;
  cursor: pointer;
  background: ${({ isPatientOnlySelected, hasSelectedReport }) => {
    if (isPatientOnlySelected) return COLOR_SYSTEM.get("Skyblue")?.[50];
    if (hasSelectedReport) return COLOR_SYSTEM.get("Gray")?.[50];
    return COLOR_SYSTEM.get("Gray")?.[0];
  }};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  ${({ isLast }) =>
    isLast &&
    `
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
    border-bottom: none;
  `}
  &:hover {
    background: ${COLOR_SYSTEM.get("Skyblue")?.[100]};
  }
`;

const PatientInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const PatientNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PatientMemo = styled.div`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Skyblue")?.[500]};
`;

const PatientMeta = styled.div`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const ReportList = styled.div`
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: 10px;
`;

const ReportItem = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: none;
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  background: ${({ selected }) =>
    selected ? COLOR_SYSTEM.get("Skyblue")?.[50] : COLOR_SYSTEM.get("Gray")?.[0]};
  border-radius: ${({ selected }) => (selected ? "12px" : "0")};
  border: ${({ selected }) =>
    selected ? `1px solid ${COLOR_SYSTEM.get("Skyblue")?.[600]}` : "none"};
  box-shadow: ${({ selected }) => (selected ? "0 2px 8px 0 rgba(33, 150, 243, 0.10)" : "none")};
  cursor: pointer;
  transition: background 0.2s, border 0.2s, bosx-shadow 0.2s;
  position: relative;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
    border-radius: 12px;

    .more-button {
      opacity: 1;
    }
  }
`;

const MoreButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s, background 0.2s;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const ReportDropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 4px;
  min-width: 160px;
  z-index: 1000;
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  border-radius: 6px;
  transition: background 0.2s;
  text-align: left;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }

  &.delete {
    color: ${COLOR_SYSTEM.get("Red")?.[600]};
  }
`;

const ReportTextBox = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ReportHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NoReport = styled.div`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  padding: 8px 0 8px 32px;
`;

const ChevronButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: ${COLOR_SYSTEM.get("Skyblue")?.[50]};
  }
`;

const StyledToggleButton = styled(ToggleButton)`
  transition: all 0.2s ease;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[20]} !important;
    transform: scale(1.05);
    cursor: pointer;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const CustomDropdownMenuItem = styled(DropdownMenuItem)<{ isSelected: boolean }>`
  padding: 6px 12px !important;
  margin: 2px 4px;
  border-radius: 6px;
  gap: 8px !important;
  background-color: ${({ isSelected }) =>
    isSelected ? COLOR_SYSTEM.get("Skyblue")?.[50] : COLOR_SYSTEM.get("Gray")?.[0]} !important;
  border: ${({ isSelected }) =>
    isSelected ? `1px solid ${COLOR_SYSTEM.get("Skyblue")?.[200]}` : "1px solid transparent"};

  &:hover {
    background-color: ${({ isSelected }) =>
      isSelected ? COLOR_SYSTEM.get("Skyblue")?.[100] : COLOR_SYSTEM.get("Gray")?.[20]} !important;
  }
`;

const sortOptions = [
  { value: "id", label: "최근 등록순" },
  { value: "updatedAt", label: "최근 수정순" },
  { value: "recent", label: "최근 보고서순" },
];

type SortOption = "id" | "updatedAt" | "recent";

interface PatientSidebarProps {
  onPatientSelect: (patientId: number) => void;
  selectedPatientId: number | null;
  selectedReportId: number | null;
  onSelectReport: (patientId: number, reportId: number) => void;
  onAddPatient: () => void;
}

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoTooltip = styled.div<{ show: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: ${COLOR_SYSTEM.get("Gray")?.[800]};
  color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: pre-line;
  max-width: 200px;
  z-index: 1000;
  opacity: ${({ show }) => (show ? 1 : 0)};
  visibility: ${({ show }) => (show ? "visible" : "hidden")};
  transition: opacity 0.2s, visibility 0.2s;

  &::before {
    content: "";
    position: absolute;
    bottom: 100%;
    right: 12px;
    border: 4px solid transparent;
    border-bottom-color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  }
`;

const InfoIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const PatientSidebar: React.FC<PatientSidebarProps> = ({
  onPatientSelect,
  selectedPatientId,
  selectedReportId,
  onSelectReport,
  onAddPatient,
}) => {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortOption>("id");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isDropdownActive, setIsDropdownActive] = useState(false);
  const [showSearchTooltip, setShowSearchTooltip] = useState(false);
  const [activeReportMenu, setActiveReportMenu] = useState<number | null>(null);
  const { patientList, patients } = useSelector((state: RootState) => state.data);
  const patientListData = patientList || [];
  const patientsData = patients || {};

  log(LogLevel.VERBOSE, "PatientSidebar", patientListData, patientsData);

  const handleToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSortSelect = (selectedSort: SortOption) => {
    setSort(selectedSort);
    setIsDropdownActive(false);
  };

  const handleDropdownToggle = () => {
    setIsDropdownActive(!isDropdownActive);
  };

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDropdownActive) {
        setIsDropdownActive(false);
      }
    };

    if (isDropdownActive) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isDropdownActive]);

  const filtered = useMemo(() => {
    let result = patientListData.filter((p) => {
      const patient = patients[p.id];
      return (
        patient.id.toString() == search ||
        patient.consultationNotes?.includes(search) ||
        patient.memo?.includes(search) ||
        patient.allergies?.includes(search) ||
        patient.diseases?.includes(search)
      );
    });
    if (sort === "id")
      result = [...result].sort((a, b) => {
        const idA = Number(a.id) || 0;
        const idB = Number(b.id) || 0;
        return idB - idA;
      });
    else if (sort === "updatedAt")
      result = [...result].sort((a, b) => {
        const aDate = patients[a.id]?.updatedAt?.toString() || "1970-01-01";
        const bDate = patients[b.id]?.updatedAt?.toString() || "1970-01-01";
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      });
    else if (sort === "recent")
      result = [...result].sort((a, b) => {
        const aDate = patients[a.id]?.lastAiReviewAt?.toString() || "1970-01-01";
        const bDate = patients[b.id]?.lastAiReviewAt?.toString() || "1970-01-01";
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      });
    return result;
  }, [patientListData, patientsData, search, sort]);

  return (
    <SidebarContainer isOpen={isSidebarOpen}>
      <HeaderSection isOpen={isSidebarOpen}>
        {isSidebarOpen && (
          <HeaderTitle>
            <Text textType="Body1SemiBold" color={COLOR_SYSTEM.get("Gray")?.[700]}>
              환자 및 보고서 목록
            </Text>
          </HeaderTitle>
        )}
        <StyledToggleButton
          type="Icon"
          icon={isSidebarOpen ? "Chevron Left" : "Chevron Right"}
          active={false}
          onClick={handleToggleSidebar}
          color="Gray"
          size="Medium"
          variant="Ghost"
          aria-label={isSidebarOpen ? "사이드바 접기" : "사이드바 펼치기"}
        />
      </HeaderSection>

      {isSidebarOpen && (
        <>
          <SearchFilterSection>
            <SearchRow>
              <SearchContainer>
                <Input
                  placeholder="환자 검색..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  leftItems={[
                    <Icon
                      name="Search"
                      size={18}
                      color={COLOR_SYSTEM.get("Gray")?.[500]}
                      key="search"
                    />,
                  ]}
                  rightItems={[
                    <InfoIconButton
                      key="info"
                      onMouseEnter={() => setShowSearchTooltip(true)}
                      onMouseLeave={() => setShowSearchTooltip(false)}
                      onClick={() => setShowSearchTooltip(!showSearchTooltip)}
                    >
                      <Icon
                        name="Information Circle"
                        size={16}
                        color={COLOR_SYSTEM.get("Gray")?.[400]}
                      />
                    </InfoIconButton>,
                  ]}
                  style={{
                    borderColor: COLOR_SYSTEM.get("Gray")?.[200],
                    backgroundColor: COLOR_SYSTEM.get("Gray")?.[0],
                    flex: 1,
                  }}
                />
                <InfoTooltip show={showSearchTooltip}>
                  검색 가능 필드:{"\n"}번호, 메모, 질병, 알레르기, 추가정보
                </InfoTooltip>
              </SearchContainer>
            </SearchRow>
            <FilterRow>
              <Dropdown
                defaultValue="정렬 방식"
                selected={
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Icon name="Swap Vertical" size={14} color={COLOR_SYSTEM.get("Gray")?.[500]} />
                    {sortOptions.find((o) => o.value === sort)?.label}
                  </div>
                }
                size="Small"
                buttonStyle="Outlined"
                onClick={handleDropdownToggle}
                active={isDropdownActive}
                buttonCustomStyle={{
                  color: COLOR_SYSTEM.get("Gray")?.[600],
                  fontSize: "13px",
                  borderColor: COLOR_SYSTEM.get("Gray")?.[200],
                  backgroundColor: COLOR_SYSTEM.get("Gray")?.[0],
                }}
                customStyle={{
                  minWidth: "120px",
                  border: `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                  padding: "4px",
                }}
              >
                {sortOptions.map((option) => (
                  <CustomDropdownMenuItem
                    key={option.value}
                    isSelected={sort === option.value}
                    onClick={() => handleSortSelect(option.value as SortOption)}
                  >
                    <Text
                      textType="Body2"
                      color={
                        sort === option.value
                          ? COLOR_SYSTEM.get("Skyblue")?.[600]
                          : COLOR_SYSTEM.get("Gray")?.[700]
                      }
                    >
                      {option.label}
                    </Text>
                  </CustomDropdownMenuItem>
                ))}
              </Dropdown>

              <Button
                type="Text"
                variant="Contained"
                size="Small"
                color="Primary"
                onClick={onAddPatient}
                style={{
                  fontSize: "13px",
                }}
              >
                +새 환자
              </Button>
            </FilterRow>
          </SearchFilterSection>

          <PatientList>
            {filtered.length === 0 && <NoReport>검색 결과가 없습니다</NoReport>}
            {filtered.map((patient) => (
              <PatientCard
                key={patient.id}
                patient={patientsData[patient.id]}
                selected={selectedPatientId === patient.id}
                selectedReportId={selectedReportId}
                onSelect={() => onPatientSelect(patient.id)}
                onReportSelect={(reportId) => onSelectReport(patient.id, reportId)}
                sort={sort}
              />
            ))}
          </PatientList>
        </>
      )}
    </SidebarContainer>
  );
};

const PatientNhisStatusTag: React.FC<{
  status?: Patient["nhisLastStatus"];
  statusAt?: string;
}> = ({ status, statusAt }) => {
  const tag = resolveNhisTag(status, statusAt);
  if (!tag) return null;
  // 카드 우측 상단에 absolute로 배치 — 이름 행 공간 침범 방지
  return (
    <NhisTagAbsolute title={tag.label}>
      <Tag size="Small" variant="Tinted" color={tag.color}>
        {tag.label}
      </Tag>
    </NhisTagAbsolute>
  );
};

const PatientCard: React.FC<{
  patient: Patient;
  selected: boolean;
  selectedReportId: number | null;
  onSelect: () => void;
  onReportSelect: (reportId: number) => void;
  sort: SortOption;
}> = ({ patient, selected, selectedReportId, onSelect, onReportSelect, sort }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [showAllReports, setShowAllReports] = useState(false);
  const [activeReportMenu, setActiveReportMenu] = useState<number | null>(null);
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [editingReviewId, setEditingReviewId] = useState<number | null>(null);

  // 보고서 선택 시 편집 모드 체크
  const handleReportClick = (reportId: number) => {
    log(LogLevel.INFO, "[PatientSidebar] 보고서 클릭", {
      reportId,
      isInEditMode: !!(window as any).__isInEditMode,
    });
    
    // 편집 모드 체크 핸들러가 있으면 호출하지 않고 바로 네빔게이션
    // onReportSelect가 차단 로직을 처리할 것임
    onReportSelect(reportId);
  };

  // 환자 선택 시 편집 모드 체크
  const handlePatientClick = () => {
    log(LogLevel.INFO, "[PatientSidebar] 환자 클릭", {
      patientId: patient.id,
      hasBlocker: !!(window as any).__navigationBlocker,
    });
    
    // 편집 모드 체크 핸들러가 있으면 호출하지 않고 바로 네비게이션
    // onSelect가 차단 로직을 처리할 것임
    onSelect();
  };
  const expandedThis = expanded;
  const age = calculateAge(patient.birthDate);
  const dispatch = useDispatch<AppDispatch>();
  const reviews = useSelector((state: RootState) => state.data.reviews);
  const { togglePin } = useAIReviewPin();
  const { updateMemo } = useAIReviewMemo();
  const { deleteReview } = useAIReviewDelete();

  const hasSelectedReport =
    selectedReportId && patient.aiReviews?.some((r) => r.id === selectedReportId);
  const isPatientOnlySelected = selected && !hasSelectedReport;

  // [2025-06-26] 추가: 보고서가 선택된 경우 해당 환자의 네비게이션을 자동으로 확장
  useEffect(() => {
    if (!selectedReportId) return;
    
    // aiReviews가 아직 로드되지 않은 경우 먼저 로드
    if (!patient.aiReviews) {
      dispatch(fetchPatientReviews(patient.id!));
      return;
    }
    
    // 선택된 리뷰가 이 환자의 것이면 탭 확장
    if (patient.aiReviews.some((r) => r.id === selectedReportId)) {
      setExpanded(true);
      
      // 선택된 보고서가 3개 이후에 있으면 자동으로 전체 목록 표시
      const selectedIndex = patient.aiReviews.findIndex((r) => r.id === selectedReportId);
      if (selectedIndex >= 3) {
        setShowAllReports(true);
      }
    }
  }, [patient.id, selectedReportId, patient.aiReviews, dispatch]);

  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();

    // 보고서 데이터가 없으면 가져오기
    if (!expanded && !patient.aiReviews) {
      dispatch(fetchPatientReviews(patient.id!));
    }

    setExpanded(!expanded);
  };

  const handleMoreClick = (e: React.MouseEvent, reviewId: number) => {
    e.stopPropagation();
    setActiveReportMenu(activeReportMenu === reviewId ? null : reviewId);
  };

  const handleToggleFavorite = async (reviewId: number) => {
    log(LogLevel.INFO, "즐겨찾기 토글", { reviewId });
    await togglePin(reviewId);
    setActiveReportMenu(null);
  };

  const handleEditMemo = (reviewId: number) => {
    setActiveReportMenu(null);
    setEditingReviewId(reviewId);
    setShowMemoModal(true);
  };

  const handleMemoModalSubmit = async (newMemo: string) => {
    if (editingReviewId) {
      log(LogLevel.INFO, "메모 저장", { reviewId: editingReviewId, memo: newMemo });
      await updateMemo(editingReviewId, newMemo);
    }
    setShowMemoModal(false);
    setEditingReviewId(null);
  };

  const handleDeleteReport = async (reviewId: number) => {
    setActiveReportMenu(null);

    ConfirmManager.show({
      title: "보고서 삭제",
      body: "이 보고서를 삭제하시겠습니까? 삭제한 보고서는 마이페이지에서 확인할 수 있습니다.",
      buttons: [
        <Button
          key="cancel"
          buttonType="button"
          type="Text"
          variant="Outlined"
          size="Medium"
          color="Tertiary"
          onClick={() => ConfirmManager.hide()}
        >
          취소
        </Button>,
        <Button
          key="delete"
          buttonType="button"
          type="Text"
          variant="Contained"
          size="Medium"
          color="Negative"
          onClick={async () => {
            log(LogLevel.INFO, "보고서 삭제 확인", { reviewId });
            ConfirmManager.hide();
            await deleteReview(reviewId, patient.id);
          }}
        >
          삭제
        </Button>,
      ],
    });
  };

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeReportMenu !== null) {
        setActiveReportMenu(null);
      }
    };

    if (activeReportMenu !== null) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [activeReportMenu]);

  return (
    <PatientCardStyled
      isPatientOnlySelected={isPatientOnlySelected}
      hasSelectedReport={!!hasSelectedReport}
    >
      <PatientNhisStatusTag
        status={patient.nhisLastStatus}
        statusAt={patient.nhisLastStatusAt}
      />
      <PatientCardHeader
        isPatientOnlySelected={isPatientOnlySelected}
        hasSelectedReport={!!hasSelectedReport}
        isLast={!expandedThis}
        onClick={handlePatientClick}
      >
        <PatientInfo>
          <PatientNameRow>
            <Icon
              name="User"
              size={18}
              color={
                isPatientOnlySelected
                  ? COLOR_SYSTEM.get("Skyblue")?.[600]
                  : hasSelectedReport
                  ? COLOR_SYSTEM.get("Gray")?.[600]
                  : COLOR_SYSTEM.get("Gray")?.[800]
              }
              style={{ marginRight: 4 }}
            />
            <Text
              textType="Body1SemiBold"
              color={
                isPatientOnlySelected
                  ? COLOR_SYSTEM.get("Skyblue")?.[600]
                  : hasSelectedReport
                  ? COLOR_SYSTEM.get("Gray")?.[600]
                  : COLOR_SYSTEM.get("Gray")?.[800]
              }
            >
              {patient.id ? `환자 ${patient.id}` : "신규 환자"}
            </Text>
            <span style={{ fontSize: 13, color: COLOR_SYSTEM.get("Gray")?.[500] }}>
              {age !== null ? `${age}세/` : ""}
              {patient.gender}
            </span>
          </PatientNameRow>
          <PatientMeta>
            AI 검토 {patient.aiReviews?.length ?? patient.aiReviewCnt ?? 0}개
            {patient.lastAiReviewAt &&
              ` (최근: ${getDateStringFromToday({ date: patient.lastAiReviewAt })})`}
          </PatientMeta>
          {sort === "id" && patient.createdAt && (
            <PatientMeta>
              등록: {getDateStringFromToday({ date: patient.createdAt })}
            </PatientMeta>
          )}
          {sort === "updatedAt" && patient.updatedAt && (
            <PatientMeta>
              수정: {getDateStringFromToday({ date: patient.updatedAt })}
            </PatientMeta>
          )}
          {patient.memo && patient.memo.trim() !== "" && (
            <PatientMemo>
              메모:{" "}
              {patient.memo.length > 15 ? `${patient.memo.substring(0, 15)}...` : patient.memo}
            </PatientMemo>
          )}
        </PatientInfo>
        <ChevronButton onClick={handleExpand}>
          <Icon
            name={expanded ? "Chevron Down" : "Chevron Right"}
            size={18}
            color={COLOR_SYSTEM.get("Gray")?.[500]}
          />
        </ChevronButton>
      </PatientCardHeader>
      {expanded && (
        <ReportList>
          {!patient.aiReviews || patient.aiReviews.length === 0 ? (
            <NoReport>AI 검토 이력이 없습니다</NoReport>
          ) : (
            (() => {
              const displayReports = showAllReports
                ? patient.aiReviews
                : patient.aiReviews.slice(0, 3);
              const hasMoreReports = patient.aiReviews.length > 3;

              return (
                <>
                  {displayReports.map((review, index) => {
                    const reviewData = reviews[review.id];
                    if (reviewData) {
                      const dateTimeStr = reviewData.reviewDate
                        ? getDateStringFromToday({ date: reviewData.reviewDate, showTime: true })
                        : "미상";

                      return (
                        <ReportItem
                          key={review.id}
                          selected={selectedReportId === review.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            log(LogLevel.INFO, "[PatientSidebar] ReportItem 클릭", {
                              reviewId: review.id,
                              selectedReportId,
                            });
                            
                            // 현재 선택된 보고서와 같은 경우 무시
                            if (selectedReportId === review.id) {
                              log(LogLevel.INFO, "[PatientSidebar] 이미 선택된 보고서, 무시");
                              return;
                            }
                            
                            // 보고서 선택만 수행 (환자 선택은 URL로 자동 처리됨)
                            handleReportClick(review.id);
                          }}
                        >
                          <Icon
                            name={reviewData.pinnedAt ? "Pin 02 Filled" : "File"}
                            size={15}
                            color={
                              reviewData.pinnedAt
                                ? COLOR_SYSTEM.get("Yellow")?.[500]
                                : COLOR_SYSTEM.get("Gray")?.[500]
                            }
                            style={{ marginRight: 4, marginTop: 2 }}
                          />
                          <ReportTextBox>
                            <ReportHeader>
                              <span>보고서 #{review.id}</span>
                              {reviewData.status === AIReviewStatus.FAILED && (
                                <Tag variant="Tinted" color="Red" size="Small">
                                  실패
                                </Tag>
                              )}
                              {/* PENDING 상태이면서 대기열 정보가 있을 때 → 대기중 */}
                              {reviewData.status === AIReviewStatus.PENDING && reviewData.queuePosition && (
                                <Tag variant="Tinted" color="Yellow" size="Small">
                                  대기중
                                </Tag>
                              )}
                              {/* PROCESSING_LLM_ANALYSIS이면서 reviewResult가 있는 경우 → 답변중 */}
                              {reviewData.status === AIReviewStatus.PROCESSING_LLM_ANALYSIS && 
                               reviewData.reviewResult && (
                                <Tag variant="Tinted" color="Green" size="Small">
                                  답변중
                                </Tag>
                              )}
                              {/* 나머지 모든 PROCESSING 상태들과 대기열 정보가 없는 PENDING → 처리중 */}
                              {((reviewData.status === AIReviewStatus.PENDING && !reviewData.queuePosition) ||
                                reviewData.status === AIReviewStatus.PROCESSING ||
                                reviewData.status === AIReviewStatus.PROCESSING_USER_INFO ||
                                reviewData.status === AIReviewStatus.PROCESSING_PRESCRIPTION ||
                                reviewData.status === AIReviewStatus.PROCESSING_DRUG_CRAWLING ||
                                reviewData.status === AIReviewStatus.PROCESSING_DRUG_INFO ||
                                reviewData.status === AIReviewStatus.PROCESSING_PROMPT_CREATION ||
                                (reviewData.status === AIReviewStatus.PROCESSING_LLM_ANALYSIS && !reviewData.reviewResult)) && (
                                <Tag variant="Tinted" color="Blue" size="Small">
                                  처리중
                                </Tag>
                              )}
                              {reviewData.status === AIReviewStatus.COMPLETED && (() => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const reviewDate = new Date(reviewData.reviewDate || reviewData.createdAt);
                                reviewDate.setHours(0, 0, 0, 0);
                                const isToday = today.getTime() === reviewDate.getTime();
                                
                                return isToday ? (
                                  <Tag variant="Tinted" color="Green" size="Small">
                                    완료
                                  </Tag>
                                ) : null;
                              })()}
                            </ReportHeader>
                            <span
                              style={{
                                color: COLOR_SYSTEM.get("Gray")?.[500],
                                fontSize: 11,
                                marginTop: 2,
                              }}
                            >
                              {dateTimeStr} · {getPromptTypeKoreanName(reviewData.promptType)}
                            </span>
                            {reviewData.memo && (
                              <Text
                                textType="Caption"
                                color={COLOR_SYSTEM.get("Skyblue")?.[500]}
                                style={{
                                  maxWidth: "100%",
                                }}
                                numberOfLines={1}
                              >
                                {reviewData.memo}
                              </Text>
                            )}
                          </ReportTextBox>
                          <MoreButton
                            className="more-button"
                            onClick={(e: React.MouseEvent) => handleMoreClick(e, review.id)}
                          >
                            <Icon
                              name="Dots Vertical"
                              size={16}
                              color={COLOR_SYSTEM.get("Gray")?.[500]}
                            />
                          </MoreButton>
                          {activeReportMenu === review.id && (
                            <ReportDropdownMenu
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                              <MenuButton onClick={() => handleToggleFavorite(review.id)}>
                                <Icon
                                  name="Pin 02 Filled"
                                  size={14}
                                  color={
                                    reviewData.pinnedAt
                                      ? COLOR_SYSTEM.get("Yellow")?.[500]
                                      : COLOR_SYSTEM.get("Gray")?.[600]
                                  }
                                />
                                {reviewData.pinnedAt ? "고정 해제" : "고정"}
                              </MenuButton>
                              <MenuButton onClick={() => handleEditMemo(review.id)}>
                                <Icon
                                  name="Pencil"
                                  size={14}
                                  color={COLOR_SYSTEM.get("Gray")?.[600]}
                                />
                                메모 수정
                              </MenuButton>
                              <MenuButton
                                className="delete"
                                onClick={() => handleDeleteReport(review.id)}
                              >
                                <Icon
                                  name="Trash"
                                  size={14}
                                  color={COLOR_SYSTEM.get("Red")?.[600]}
                                />
                                삭제
                              </MenuButton>
                            </ReportDropdownMenu>
                          )}
                        </ReportItem>
                      );
                    }
                    return null;
                  })}

                  {hasMoreReports && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "8px 0",
                        borderTop: `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
                        marginTop: "4px",
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAllReports(!showAllReports);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: COLOR_SYSTEM.get("Skyblue")?.[600],
                          fontSize: "12px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            COLOR_SYSTEM.get("Skyblue")?.[50] || "";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "none";
                        }}
                      >
                        <Text
                          textType="Body2"
                          color={COLOR_SYSTEM.get("Skyblue")?.[600]}
                          style={{ fontSize: "12px" }}
                        >
                          {showAllReports ? "접기" : `더보기 (+${patient.aiReviews.length - 3})`}
                        </Text>
                        <Icon
                          name={showAllReports ? "Chevron Up" : "Chevron Down"}
                          size={12}
                          color={COLOR_SYSTEM.get("Skyblue")?.[600]}
                        />
                      </button>
                    </div>
                  )}
                </>
              );
            })()
          )}
        </ReportList>
      )}

      {/* 메모 수정 모달 */}
      <MemoEditModal
        isOpen={showMemoModal}
        onClose={() => {
          setShowMemoModal(false);
          setEditingReviewId(null);
        }}
        onSubmit={handleMemoModalSubmit}
        title="보고서 메모 수정"
        initialMemo={editingReviewId ? reviews[editingReviewId]?.memo : ""}
      />
    </PatientCardStyled>
  );
};

export default PatientSidebar;
